import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';

import {
  CloudUploadOutlined,
  FilterListOutlined,
  SearchOutlined
} from '@mui/icons-material';

import CommonTable from '../../../../Common/components/Listing/CommonTable';

import UploadDoc from './Modal_UploadDoc';

import { columns, patientRows } from './data';

import {
  searchFieldSx,
  filterButtonSx,
  patientCountChipSx,
  providerNameSx,
  cardSx,
  cardContentSx,
  toolbarSx,
  providerHeaderSx
} from '../../../../Common/components/Styles/InputStyles';

const Details = ({ className, ...rest }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openUpload, setOpenUpload] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const rows = useMemo(
    () =>
      patientRows.map((row, index) => ({
        id: index + 1,
        pname: row[0],
        dob: row[1],
        mr: row[2]
      })),
    []
  );

  const filteredRows = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    if (!searchValue) {
      return rows;
    }

    return rows.filter(row =>
      row.pname.toLowerCase().includes(searchValue)
    );
  }, [rows, search]);

  const visibleRows = useMemo(() => {
    const startIndex = page * rowsPerPage;

    return filteredRows.slice(
      startIndex,
      startIndex + rowsPerPage
    );
  }, [filteredRows, page, rowsPerPage]);

  const handleSearchChange = event => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleUploadOpen = () => {
    setOpenUpload(true);
  };

  const handleUploadClose = () => {
    setOpenUpload(false);
  };

  return (
    <>
      <Card
        {...rest}
        className={className}
        sx={cardSx}>
        <Box sx={{ p: 0 }}>
          <Box sx={providerHeaderSx}>
            <Typography sx={providerNameSx}>
              Dr. Charles
            </Typography>

            <Chip
              label={`${filteredRows.length} Patients`}
              size="small"
              sx={patientCountChipSx}
            />
          </Box>

          <Box sx={toolbarSx}>
            <TextField
              size="small"
              placeholder="Search patients..."
              value={search}
              onChange={handleSearchChange}
              sx={{
                ...searchFieldSx,
                width: 320
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined
                        sx={{
                          color: '#757575',
                          fontSize: 18
                        }}
                      />
                    </InputAdornment>
                  )
                }
              }}
            />

            <Button
              variant="outlined"
              startIcon={<FilterListOutlined />}
              sx={filterButtonSx}>
              Filter
            </Button>
          </Box>
        </Box>

        <CardContent sx={cardContentSx}>
          <CommonTable
            columns={columns}
            rows={visibleRows}
            totalCount={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 75, 100]}
            emptyText="No patients found."
            loading={loading}
            skeletonRows={10}
            actionWrapper
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onActionClick={handleUploadOpen}
            renderAction={() => (
              <CloudUploadOutlined fontSize="small" />
            )}
          />
        </CardContent>
      </Card>

      <UploadDoc
        open={openUpload}
        onClose={handleUploadClose}
      />
    </>
  );
};

Details.propTypes = {
  className: PropTypes.string
};

export default Details;